import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeader, removeToken } from '../../utils/auth';
import { ArrowLeft, Mail, Phone, MapPin, Calendar, User, Tag, FileText, CheckCircle, UserPlus } from 'lucide-react';
import '../../styles/admin/LeadDetail.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeadDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    fetchLeadDetails();
  }, [leadId]);

  const fetchLeadDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/leads/${leadId}`,
        { headers: getAuthHeader() }
      );
      setLead(response.data.lead);
      setNotes(response.data.lead.notes_internes || '');
    } catch (error) {
      if (error.response?.status === 401) {
        removeToken();
        navigate('/admin');
      } else {
        console.error('Erreur:', error);
        alert('Erreur lors du chargement du lead');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      await axios.put(
        `${API_URL}/api/leads/${leadId}`,
        { notes_internes: notes },
        { headers: getAuthHeader() }
      );
      alert('✅ Notes sauvegardées!');
      fetchLeadDetails();
    } catch (error) {
      alert('❌ Erreur lors de la sauvegarde');
      console.error(error);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleConvertToClient = async () => {
    if (!window.confirm(`Convertir "${lead.prenom} ${lead.nom}" en client?\n\nCela créera une fiche client complète.`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/leads/convert-to-client`,
        {
          lead_id: leadId,
          type_client: 'residentiel'
        },
        { headers: getAuthHeader() }
      );

      alert(`✅ Lead converti en client!\n\nNuméro client: ${response.data.client.numero_client}`);
      
      // Rediriger vers la fiche client
      navigate(`/admin/erp/client/${response.data.client.id}`);
    } catch (error) {
      alert('❌ Erreur lors de la conversion');
      console.error(error);
    }
  };

  const getStatutBadgeClass = (statut) => {
    const classes = {
      'nouveau': 'badge-nouveau',
      'a_qualifier': 'badge-a-qualifier',
      'qualifie': 'badge-qualifie',
      'converti': 'badge-converti',
      'perdu': 'badge-perdu',
      'a_relancer': 'badge-a-relancer'
    };
    return classes[statut] || 'badge-default';
  };

  const getStatutLabel = (statut) => {
    const labels = {
      'nouveau': 'Nouveau',
      'a_qualifier': 'À qualifier',
      'qualifie': 'Qualifié',
      'converti': 'Converti',
      'perdu': 'Perdu',
      'a_relancer': 'À relancer'
    };
    return labels[statut] || statut;
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  if (!lead) {
    return <div className="admin-loading">Lead non trouvé</div>;
  }

  return (
    <div className="lead-detail-page">
      <div className="lead-detail-header">
        <div className="header-content">
          <Link to="/admin/leads" className="back-link">
            <ArrowLeft size={20} />
            Retour aux leads
          </Link>
          <div className="header-title">
            <h1>🎯 {lead.prenom} {lead.nom}</h1>
            <span className={`status-badge-large ${getStatutBadgeClass(lead.statut)}`}>
              {getStatutLabel(lead.statut)}
            </span>
          </div>
          <div className="header-actions">
            {lead.statut !== 'converti' && (
              <button className="btn-convert-primary" onClick={handleConvertToClient}>
                <UserPlus size={20} />
                Convertir en client
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="lead-detail-content">
        <div className="lead-detail-grid">
          {/* Colonne gauche - Informations */}
          <div className="lead-column">
            {/* Informations de contact */}
            <div className="info-card">
              <h3>📞 Contact</h3>
              <div className="info-list">
                <div className="info-item">
                  <Mail size={18} />
                  <div>
                    <span className="info-label">Email</span>
                    <a href={`mailto:${lead.courriel}`} className="info-value">{lead.courriel}</a>
                  </div>
                </div>
                {lead.telephone && (
                  <div className="info-item">
                    <Phone size={18} />
                    <div>
                      <span className="info-label">Téléphone</span>
                      <a href={`tel:${lead.telephone}`} className="info-value">{lead.telephone}</a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Adresse */}
            <div className="info-card">
              <h3>📍 Adresse</h3>
              <div className="info-list">
                <div className="info-item">
                  <MapPin size={18} />
                  <div>
                    <span className="info-label">Adresse complète</span>
                    <span className="info-value">{lead.adresse_complete}</span>
                  </div>
                </div>
                {lead.ville && (
                  <div className="info-item">
                    <div style={{ width: '18px' }}></div>
                    <div>
                      <span className="info-label">Ville</span>
                      <span className="info-value">{lead.ville}</span>
                    </div>
                  </div>
                )}
                {lead.code_postal && (
                  <div className="info-item">
                    <div style={{ width: '18px' }}></div>
                    <div>
                      <span className="info-label">Code postal</span>
                      <span className="info-value">{lead.code_postal}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Propriété */}
            {lead.superficie_terrain && (
              <div className="info-card">
                <h3>🏡 Propriété</h3>
                <div className="info-list">
                  <div className="info-item">
                    <div style={{ width: '18px' }}></div>
                    <div>
                      <span className="info-label">Superficie</span>
                      <span className="info-value">{lead.superficie_terrain.toLocaleString('fr-CA')} pi²</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Services demandés */}
            {lead.services_souhaites && lead.services_souhaites.length > 0 && (
              <div className="info-card">
                <h3>💼 Services demandés</h3>
                <div className="services-tags">
                  {lead.services_souhaites.map((service, idx) => (
                    <span key={idx} className="service-tag">{service}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Commentaire client */}
            {lead.commentaire_client && (
              <div className="info-card">
                <h3>💬 Commentaire du client</h3>
                <p className="comment-text">{lead.commentaire_client}</p>
              </div>
            )}
          </div>

          {/* Colonne droite - Suivi */}
          <div className="lead-column">
            {/* Informations de suivi */}
            <div className="info-card">
              <h3>📊 Informations de suivi</h3>
              <div className="info-list">
                <div className="info-item">
                  <User size={18} />
                  <div>
                    <span className="info-label">Numéro lead</span>
                    <span className="info-value">{lead.numero_lead}</span>
                  </div>
                </div>
                <div className="info-item">
                  <Tag size={18} />
                  <div>
                    <span className="info-label">Source</span>
                    <span className="info-value">
                      {lead.source === 'site_web' ? 'Site web' : 
                       lead.source === 'facebook' ? 'Facebook' :
                       lead.source === 'google' ? 'Google' :
                       lead.source === 'referral' ? 'Référence' :
                       lead.source}
                    </span>
                  </div>
                </div>
                <div className="info-item">
                  <Calendar size={18} />
                  <div>
                    <span className="info-label">Date de création</span>
                    <span className="info-value">
                      {new Date(lead.date_creation).toLocaleDateString('fr-CA', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
                {lead.date_conversion && (
                  <div className="info-item">
                    <CheckCircle size={18} />
                    <div>
                      <span className="info-label">Date de conversion</span>
                      <span className="info-value">
                        {new Date(lead.date_conversion).toLocaleDateString('fr-CA')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notes internes */}
            <div className="info-card">
              <h3>📝 Notes internes</h3>
              <textarea
                className="notes-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ajoutez des notes sur ce lead..."
                rows="8"
              />
              <button 
                className="btn-save-notes"
                onClick={handleSaveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? 'Sauvegarde...' : '💾 Sauvegarder les notes'}
              </button>
            </div>

            {/* Actions */}
            {lead.statut !== 'converti' && (
              <div className="info-card actions-card">
                <h3>⚡ Actions</h3>
                <div className="actions-list">
                  <button className="action-btn primary" onClick={handleConvertToClient}>
                    <UserPlus size={20} />
                    <span>Convertir en client</span>
                  </button>
                </div>
              </div>
            )}

            {/* Si converti */}
            {lead.statut === 'converti' && lead.converti_en_client_id && (
              <div className="info-card success-card">
                <h3>✅ Lead converti</h3>
                <p>Ce lead a été converti en client.</p>
                <Link 
                  to={`/admin/erp/client/${lead.converti_en_client_id}`}
                  className="btn-view-client"
                >
                  Voir la fiche client
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetail;
